/* ============================================================
   hearo — PDP Scroll Effects
   --------------------------------------------------------------
   Vanilla JS, zero dependencies. Provides:
     1. Scroll-reveal fade+translateY on [data-hearo-reveal]
     2. Stagger-children on [data-hearo-stagger] > *
     3. Auto-attach reveal to all .shopify-section siblings after
        the .hearo-pdp hero (so existing homepage sections animate
        in PDP context without editing them individually)
     4. Sticky ATC-bar observer (shows [data-hearo-sticky-atc]
        when the hero .hearo-pdp__atc scrolls out of the viewport)

   Doctrine compliance (00-CUSTOMER-JOURNEY-DOCTRINE.md §8):
   - No parallax, no auto-play, no hover-only information.
   - prefers-reduced-motion disables all animations (elements
     appear instantly in their final state).
   - IntersectionObserver only — no scroll listeners.
   ============================================================ */

(function () {
  'use strict';

  // Guard against duplicate init if section is re-rendered
  if (window.__hearoPdpEffectsInit) return;
  window.__hearoPdpEffectsInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* --- Utility: mark everything revealed (fallback / reduce) --- */
  function revealAll() {
    var nodes = document.querySelectorAll('[data-hearo-reveal]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      el.classList.add('is-in');
      if (el.hasAttribute('data-hearo-stagger')) {
        var kids = el.children;
        for (var k = 0; k < kids.length; k++) {
          kids[k].classList.add('is-in');
        }
      }
    }
  }

  /* --- Auto-attach reveal attribute to sibling sections --- */
  /* The .hearo-pdp hero is inside a .shopify-section wrapper.
     Every following .shopify-section is a homepage/PDP section
     the user wants to see animate as they scroll. We attach the
     reveal attribute to their wrapper so the observer picks them
     up — this avoids editing each section file individually. */
  function autoAttachReveal() {
    var heroInternal = document.querySelector('.hearo-pdp');
    if (!heroInternal) return;
    var heroWrapper = heroInternal.closest('.shopify-section');
    if (!heroWrapper) return;

    var next = heroWrapper.nextElementSibling;
    while (next) {
      if (
        next.classList &&
        next.classList.contains('shopify-section') &&
        !next.querySelector('.hearo-sticky-atc') // never animate the sticky-atc wrapper
      ) {
        if (!next.hasAttribute('data-hearo-reveal')) {
          next.setAttribute('data-hearo-reveal', '');
        }
      }
      next = next.nextElementSibling;
    }
  }

  /* --- Reveal observer --- */
  function initReveals() {
    autoAttachReveal();

    if (reduceMotion || !hasIO) {
      revealAll();
      return;
    }

    var revealObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          var el = entry.target;
          el.classList.add('is-in');
          revealObserver.unobserve(el);

          if (el.hasAttribute('data-hearo-stagger')) {
            var kids = el.children;
            for (var k = 0; k < kids.length; k++) {
              (function (child, index) {
                child.style.transitionDelay = index * 80 + 'ms';
                window.requestAnimationFrame(function () {
                  child.classList.add('is-in');
                });
              })(kids[k], k);
            }
          }
        }
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -48px 0px'
      }
    );

    var nodes = document.querySelectorAll('[data-hearo-reveal]');
    for (var i = 0; i < nodes.length; i++) {
      revealObserver.observe(nodes[i]);
    }
  }

  /* --- Sticky ATC observer --- */
  /* Watches .hearo-pdp__atc (the hero add-to-cart button). When
     it leaves the viewport (user scrolled past it), the sticky
     bar [data-hearo-sticky-atc] slides up into view. */
  function initStickyATC() {
    var stickyBar = document.querySelector('[data-hearo-sticky-atc]');
    var heroATC = document.querySelector('.hearo-pdp__atc');
    if (!stickyBar || !heroATC) return;

    if (!hasIO) {
      // Fallback: always visible on very old browsers
      stickyBar.classList.add('is-visible');
      return;
    }

    var atcObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
          } else {
            stickyBar.classList.add('is-visible');
          }
        }
      },
      {
        threshold: 0,
        rootMargin: '-96px 0px 0px 0px'
      }
    );

    atcObserver.observe(heroATC);
  }

  /* --- Rating → scroll to reviews section -------------------- */
  /* The rating block in the info-card is an <a href="#...">. Native
     anchor navigation jumps instantly; we intercept to provide a
     smooth scroll. Falls back gracefully if the reviews section
     is missing. */
  function initRatingScroll() {
    var ratingLinks = document.querySelectorAll('[data-scroll-to-reviews]');
    if (ratingLinks.length === 0) return;

    function findReviewsTarget() {
      return (
        document.getElementById('shopify-section-hearo_reviews') ||
        document.querySelector('.section-hearo-reviews') ||
        document.querySelector('.hearo-reviews')
      );
    }

    ratingLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = findReviewsTarget();
        if (!target) return; // let native anchor behaviour kick in
        e.preventDefault();
        var behavior = reduceMotion ? 'auto' : 'smooth';
        target.scrollIntoView({ behavior: behavior, block: 'start' });
        // Move focus to the section for screen-reader continuity
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: true });
      });
    });
  }

  /* --- Discount modal ---------------------------------------- */
  /* Apply button opens a native <dialog> with an email capture
     form. On submit we POST to Shopify's customer endpoint
     (/contact, form_type=customer, fire-and-forget) then redirect
     to /discount/{code} which applies the code to the cart. The
     new-customer-only rule is enforced on the Shopify discount
     itself (customer eligibility setting), not in this JS. */
  function initDiscountModal() {
    var applyBtns = document.querySelectorAll('[data-discount-apply]');
    if (applyBtns.length === 0) return;

    applyBtns.forEach(function (applyBtn) {
      var modalId = applyBtn.getAttribute('aria-controls');
      var modal = modalId ? document.getElementById(modalId) : null;
      if (!modal) return;

      var closeBtn = modal.querySelector('[data-modal-close]');
      var form = modal.querySelector('form');
      var emailInput = modal.querySelector('[data-modal-email]');
      var submitBtn = modal.querySelector('.hearo-pdp__modal-submit');
      var code = applyBtn.getAttribute('data-discount-code') || '';

      function openModal() {
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          // Fallback for browsers without <dialog> support
          modal.setAttribute('open', '');
          modal.style.position = 'fixed';
          modal.style.top = '50%';
          modal.style.left = '50%';
          modal.style.transform = 'translate(-50%, -50%)';
          modal.style.zIndex = '100';
        }
        // Focus email input after paint so the close button isn't focused first
        window.requestAnimationFrame(function () {
          if (emailInput) emailInput.focus();
        });
      }

      function closeModal() {
        if (typeof modal.close === 'function') {
          modal.close();
        } else {
          modal.removeAttribute('open');
          modal.style.display = 'none';
        }
      }

      applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          closeModal();
        });
      }

      // Close on backdrop click (target is the dialog itself, not its children)
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });

      if (!form) return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var email = emailInput ? emailInput.value.trim() : '';
        if (!email) {
          if (emailInput) emailInput.focus();
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }

        var redirectUrl = code ? '/discount/' + encodeURIComponent(code) : '/cart';
        var action = form.getAttribute('action') || '/contact';
        var formData = new FormData(form);

        // Store email locally so analytics / later flows can pick it up
        try { localStorage.setItem('hearo_newsletter_email', email); } catch (_) {}

        // Fire-and-forget POST to Shopify customer endpoint, then redirect
        // regardless of success — the user should always get their discount.
        function redirect() {
          window.location.href = redirectUrl;
        }

        if (typeof fetch === 'function') {
          fetch(action, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            headers: { Accept: 'text/html,application/json' }
          })
            .then(redirect)
            .catch(redirect);

          // Safety net: if the fetch hangs, redirect after 3s anyway
          setTimeout(redirect, 3000);
        } else {
          // Very old browser — just redirect, skip newsletter
          redirect();
        }
      });
    });
  }

  /* --- Boot --- */
  function init() {
    initReveals();
    initStickyATC();
    initRatingScroll();
    initDiscountModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
